import React, { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { useParams, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  TextInput,
  Select,
  Text,
  Button,
  Box,
  Group,
  Stack,
  Card,
  Avatar,
  ScrollArea,
} from "@mantine/core";
import { dialCodes, MandateFormValues } from "../../types/types";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import classes from "./MandateCheckout.module.css";
import AddPayBanner from "../../assets/img/AddPay.jpeg";
import formIcon from "../../assets/img/form.png";

async function fetchMandateData(
  reference_id: string
): Promise<Partial<MandateFormValues>> {
  // Replace the URL with your actual API endpoint
  const response = await fetch(
    `/server/mandate_service/mandate-data?reference_id=${reference_id}`
  );
  if (!response.ok) throw new Error("Failed to fetch mandate data");
  return response.json();
}

async function fetchFieldOptions(reference_id: string): Promise<any> {
  // Replace the URL with your actual API endpoint
  const response = await fetch(
    `/server/mandate_service/mandate-field-options?reference_id=${reference_id}`
  );
  if (!response.ok) throw new Error("Failed to fetch mandate data");
  return await response.json();
}

// Mantine's `required` prop alone doesn't block submission for every input type
// (notably `Select`), so every mandatory field needs an explicit validate rule.
const requiredField = (label: string) => (value: string) =>
  value && value.trim().length > 0 ? null : `${label} is required`;

export default function MandatePage() {
  const { reference_id } = useParams<{ reference_id: string }>();
  const [searchParams] = useSearchParams();
  //const type = searchParams.get("type") || "";
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [accountTypes, setAccountTypes] = useState<
    { key: string; label: string }[]
  >([]);
  const [banks, setBanks] = useState<{ key: string; label: string }[]>([]);
  const [holoderTypes, setHolderTypes] = useState<
    { key: string; label: string }[]
  >([]);
  const [firstNameDisabled, setFirstNameDisabled] = useState(true);
  const [lastNameDisabled, setLastNameDisabled] = useState(true);

  const form = useForm<MandateFormValues>({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      bank_name: "",
      bank_account_number: "",
      bank_account_branch_code: "",
      bank_account_type: "",
      bank_account_holder_type: "",
      bank_account_holder_name: "",
      bank_account_holder_id: "",
      phone_dial_code: "",
      phone_dial_number: "",
      commences_at: "",
      currency_code: "",
      recurring_amount: 0,
    },
    validate: {
      first_name: requiredField("First Name"),
      last_name: requiredField("Last Name"),
      bank_account_number: requiredField("Account Number"),
      bank_account_type: requiredField("Account Type"),
      bank_name: requiredField("Bank"),
      bank_account_branch_code: requiredField("Branch Code"),
      bank_account_holder_type: requiredField("Identity Type"),
      bank_account_holder_id: requiredField("Identity Number"),
      bank_account_holder_name: requiredField("Account Holder Name"),
      phone_dial_code: requiredField("Mobile Country Code"),
      phone_dial_number: requiredField("Mobile Number"),
    },
  });

  useEffect(() => {
    console.log("Reference ID:", reference_id);
    //console.log("Type:", type);
    if (reference_id) {
      fetchMandateData(reference_id)
        .then((data) => {
          form.setValues(data);
          setFirstNameDisabled(data.first_name !== "");
          setLastNameDisabled(data.last_name !== "");
        })
        .catch((error) => {
          // Handle error as needed
          console.error(error);
        });
      fetchFieldOptions(reference_id)
        .then((data) => {
          setAccountTypes(data.bank_account_types || []);
          setBanks(data.bank_names || []);
          setHolderTypes(data.bank_account_holder_types || []);
        })
        .catch((error) => {
          console.error("Error fetching field options:", error);
        });
      setLoading(false);
    }
  }, [reference_id]);

  useEffect(() => {
    const selectedBank = form.values.bank_name;
    if (selectedBank) {
      form.setFieldValue("bank_account_branch_code", selectedBank);
    }
  }, [form.values.bank_name]);

  const mapOptions = (arr: { key: string; label: string }[]) =>
    arr.map(({ key, label }) => ({ value: key, label }));

  const handleSubmit = async (values: MandateFormValues) => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `/server/mandate_service/mandate-data?reference_id=${reference_id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(values),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to submit mandate data");
      }
      const result = await response.json();
      console.log("Mandate submitted:", result);
      setMessage(result.message);
      if (result && result.redirect) {
        window.location.href = result.redirect;
      }
      // Optionally show a success message or redirect
    } catch (error: any) {
      console.error("Submission error:", error);
      // Optionally show an error message to the user
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      style={() => ({
        width: "100vw",
        height: "100vh",
      })}
    >
      <Card
        withBorder
        maw={600}
        mx="auto"
        className={classes.card}
        style={{
          backgroundColor: "rgba(255, 255, 255, 0)",
        }}
      >
        <Card.Section h={170} p="sm" bg="black">
          <Box
            style={{
              backgroundImage: `url(${AddPayBanner})`,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              height: "100%",
              width: "100%",
            }}
          />
        </Card.Section>
        <Avatar
          src={formIcon}
          size={80}
          radius={80}
          mx="auto"
          mt={-30}
          className={classes.avatar}
          style={{ backgroundColor: "white" }}
        />
        <Text ta="center" fz="lg" fw={500} mt="sm" pb="md">
          Mandate Form
        </Text>
        {!loading && !message ? (
          <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
            <ScrollArea h={500}>
              <Stack>
                <Group grow>
                  <TextInput
                    label="First Name"
                    placeholder="First Name"
                    {...form.getInputProps("first_name")}
                    disabled={firstNameDisabled}
                    required
                  />
                  <TextInput
                    label="Last Name"
                    placeholder="Last Name"
                    {...form.getInputProps("last_name")}
                    disabled={lastNameDisabled}
                    required
                  />
                </Group>

                <Group grow>
                  <TextInput
                    label="Account Number"
                    placeholder="Account Number"
                    {...form.getInputProps("bank_account_number")}
                    required
                  />

                  <Select
                    label="Account Type"
                    data={mapOptions(accountTypes)}
                    {...form.getInputProps("bank_account_type")}
                    required
                  />
                </Group>

                <Group grow>
                  <Select
                    label="Bank"
                    data={mapOptions(banks)}
                    {...form.getInputProps("bank_name")}
                    required
                  />

                  <TextInput
                    label="Branch Code"
                    placeholder="Branch Code"
                    {...form.getInputProps("bank_account_branch_code")}
                    required
                  />
                </Group>

                <Group grow>
                  <Select
                    label="Identity Type"
                    data={mapOptions(holoderTypes)}
                    {...form.getInputProps("bank_account_holder_type")}
                    required
                  />

                  <TextInput
                    label="Identity Number"
                    placeholder="Identity Number"
                    {...form.getInputProps("bank_account_holder_id")}
                    required
                  />
                </Group>

                <TextInput
                  label="Account Holder Name"
                  placeholder="Account Holder Name"
                  {...form.getInputProps("bank_account_holder_name")}
                  required
                />

                <Group grow>
                  <Select
                    label="Mobile Country Code"
                    data={dialCodes}
                    {...form.getInputProps("phone_dial_code")}
                    required
                  />
                  <TextInput
                    label="Mobile Number"
                    placeholder="Mobile Number"
                    {...form.getInputProps("phone_dial_number")}
                    required
                  />
                </Group>

                <TextInput
                  label="Email"
                  placeholder="Email"
                  {...form.getInputProps("email")}
                  disabled
                />

                <Group grow>
                  <TextInput
                    label="Currency"
                    {...form.getInputProps("currency_code")}
                    disabled
                  />
                  <TextInput
                    label="Collection Amount"
                    type="number"
                    {...form.getInputProps("recurring_amount")}
                    disabled
                  />
                </Group>

                <TextInput
                  label="Commences At"
                  placeholder="YYYY-MM-DD"
                  {...form.getInputProps("commences_at")}
                  disabled
                />
              </Stack>
            </ScrollArea>
            <Group justify="center" mt="md">
              <Button
                type="submit"
                radius="xl"
                w="40%"
                variant="gradient"
                gradient={{ from: "#00ddb3", to: "#00ddb3", deg: 125 }}
                loading={submitting}
                disabled={submitting}
                loaderProps={{ color: "white" }}
              >
                Submit
              </Button>
            </Group>
          </form>
        ) : loading ? (
          <LoadingSpinner color="blue" size="md" h="50vh" />
        ) : message && !loading ? (
          <Group justify="center" mt="md">
            <Text pl="2%">{message}</Text>
            <Button
              component={Link}
              to="/app"
              radius="xl"
              w="40%"
              variant="gradient"
              gradient={{ from: "#00ddb3", to: "#00ddb3", deg: 125 }}
            >
              Return To App
            </Button>
          </Group>
        ) : null}
      </Card>
    </Box>
  );
}
